<script setup lang="ts">
import Card from '@/Components/Ui/Card.vue';
import Progress from '@/Components/Ui/Progress.vue';
import { usePage } from '@inertiajs/vue3';
import {
    ArrowDownLeft,
    ArrowUpRight,
    ShoppingBasket,
    TramFront,
} from 'lucide-vue-next';

const page = usePage();
const money = (cents: number) =>
    new Intl.NumberFormat(page.props.app.lang === 'en' ? 'en-IE' : 'fr-FR', {
        style: 'currency',
        currency: 'EUR',
        maximumFractionDigits: cents % 100 === 0 ? 0 : 2,
    }).format(cents / 100);
const budgets = [
    { key: 'food', spent: 32000, limit: 45000, color: 'positive' as const },
    { key: 'leisure', spent: 8500, limit: 15000, color: 'terracotta' as const },
];
</script>

<template>
    <figure
        id="apercu"
        class="scroll-mt-8"
        :aria-label="$t('landing.preview.label')"
    >
        <div
            class="border-border bg-surface-2 relative rounded-2xl border p-2 sm:p-5 lg:p-7"
        >
            <Card padding="none" class="overflow-hidden">
                <div
                    class="border-border flex flex-wrap items-center justify-between gap-3 border-b px-4 py-4 sm:px-7"
                >
                    <div class="flex flex-wrap items-center gap-2 text-sm">
                        <span class="font-semibold">{{
                            $t('landing.preview.household')
                        }}</span>
                        <span aria-hidden="true" class="text-muted">/</span>
                        <span class="text-text-soft">{{
                            $t('landing.preview.overview')
                        }}</span>
                    </div>
                    <span
                        class="border-border bg-canvas rounded-md border px-3 py-1.5 text-xs"
                        >{{ $t('landing.preview.month') }}</span
                    >
                </div>
                <dl
                    class="grid gap-5 px-4 py-7 sm:grid-cols-3 sm:gap-8 sm:px-7 sm:py-8"
                >
                    <div>
                        <dt
                            class="text-text-soft flex items-center gap-2 text-xs"
                        >
                            <ArrowDownLeft
                                class="text-positive size-4"
                                aria-hidden="true"
                            />{{ $t('landing.preview.income') }}
                        </dt>
                        <dd
                            class="font-geist-mono mt-2 text-2xl tracking-tight sm:text-3xl lg:text-4xl"
                        >
                            {{ money(320000) }}
                        </dd>
                    </div>
                    <div>
                        <dt
                            class="text-text-soft flex items-center gap-2 text-xs"
                        >
                            <ArrowUpRight
                                class="text-negative size-4"
                                aria-hidden="true"
                            />{{ $t('landing.preview.expenses') }}
                        </dt>
                        <dd
                            class="font-geist-mono mt-2 text-2xl tracking-tight sm:text-3xl lg:text-4xl"
                        >
                            {{ money(234000) }}
                        </dd>
                    </div>
                    <div
                        class="border-border border-t pt-4 sm:border-t-0 sm:border-l sm:pt-0 sm:pl-7"
                    >
                        <dt class="text-text-soft text-xs">
                            {{ $t('landing.preview.balance') }}
                        </dt>
                        <dd
                            class="font-geist-mono text-positive mt-2 text-2xl tracking-tight sm:text-3xl lg:text-4xl"
                        >
                            + {{ money(86000) }}
                        </dd>
                    </div>
                </dl>
                <div
                    class="border-border grid gap-8 border-t px-4 py-6 sm:px-7 lg:grid-cols-2 lg:gap-12"
                >
                    <div>
                        <p class="mb-2 text-sm font-semibold">
                            {{ $t('landing.preview.recent') }}
                        </p>
                        <div
                            v-for="transaction in [
                                {
                                    key: 'groceries',
                                    category: 'food',
                                    icon: ShoppingBasket,
                                    amount: 6480,
                                },
                                {
                                    key: 'transport',
                                    category: 'travel',
                                    icon: TramFront,
                                    amount: 3200,
                                },
                            ]"
                            :key="transaction.key"
                            class="border-border flex items-center gap-3 border-b py-3 last:border-0"
                        >
                            <span
                                class="bg-surface-2 hidden size-9 shrink-0 items-center justify-center rounded-lg sm:flex"
                                ><component
                                    :is="transaction.icon"
                                    class="text-text-soft size-4"
                                    aria-hidden="true"
                            /></span>
                            <div class="min-w-0 flex-1">
                                <p class="text-sm">
                                    {{
                                        $t(`landing.preview.${transaction.key}`)
                                    }}
                                </p>
                                <p class="text-text-soft mt-1 text-xs">
                                    {{
                                        $t(
                                            `landing.preview.${transaction.category}`,
                                        )
                                    }}
                                </p>
                            </div>
                            <span
                                class="font-geist-mono shrink-0 text-xs sm:text-sm"
                                >− {{ money(transaction.amount) }}</span
                            >
                        </div>
                    </div>
                    <div>
                        <p class="mb-4 text-sm font-semibold">
                            {{ $t('landing.preview.budgets') }}
                        </p>
                        <div
                            v-for="budget in budgets"
                            :key="budget.key"
                            class="mt-5 first:mt-0"
                        >
                            <div
                                class="mb-2 flex flex-wrap justify-between gap-2 text-xs"
                            >
                                <span>{{
                                    $t(`landing.preview.${budget.key}`)
                                }}</span
                                ><span class="font-geist-mono text-text-soft"
                                    >{{ money(budget.spent) }} /
                                    {{ money(budget.limit) }}</span
                                >
                            </div>
                            <Progress
                                :value="(budget.spent / budget.limit) * 100"
                                :color="budget.color"
                                :aria-label="
                                    $t(`landing.preview.${budget.key}`)
                                "
                                :aria-valuetext="`${money(budget.spent)} ${$t('landing.preview.spent')} ${money(budget.limit)}`"
                            />
                        </div>
                    </div>
                </div>
            </Card>
        </div>
        <figcaption
            class="text-text-soft mt-4 flex flex-col justify-between gap-2 text-xs leading-relaxed sm:flex-row sm:gap-8"
        >
            <span class="shrink-0">{{ $t('landing.preview.caption') }}</span
            ><span class="max-w-lg sm:text-right">{{
                $t('landing.preview.balance_note')
            }}</span>
        </figcaption>
    </figure>
</template>
